import { useEffect, useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import {
  Plus,
  Trash2,
  Edit,
  BookOpen,
  Tag,
  HelpCircle,
  CheckCircle2,
  XCircle,
  Database,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import {
  sqlRagApi,
  DataSource,
  GlossaryTerm,
  CreateGlossaryTermRequest,
  UpdateGlossaryTermRequest,
} from "@/services/api/sqlRag";

export const Route = createFileRoute("/portal/knowledgebase-sql/glossary")({
  component: GlossaryPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      dataSource: (search.dataSource as string) || undefined,
    };
  },
});

const CATEGORIES = [
  {
    value: "financial",
    label: "Financial",
    description: "Revenue, cost, profit terms",
  },
  {
    value: "customer",
    label: "Customer",
    description: "Customer identification terms",
  },
  {
    value: "product",
    label: "Product",
    description: "Product and inventory terms",
  },
  { value: "time", label: "Time", description: "Date and time period terms" },
  { value: "sales", label: "Sales", description: "Sales and order terms" },
  { value: "general", label: "General", description: "Other business terms" },
];

function GlossaryPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  const canCreateGlossary = can("create", "kb-sql");
  const canEditGlossary = can("edit", "kb-sql");
  const canDeleteGlossary = can("delete", "kb-sql");
  const search = useSearch({ from: "/portal/knowledgebase-sql/glossary" });
  const selectedDataSourceId = search.dataSource || "";

  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loadingDataSources, setLoadingDataSources] = useState(true);
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState<GlossaryTerm[]>([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeSource, setActiveSource] = useState<"all" | "admin" | "learned">(
    "all",
  );
  const [currentDataSourceId, setCurrentDataSourceId] =
    useState(selectedDataSourceId);

  // Create/Edit dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingTerm, setEditingTerm] = useState<GlossaryTerm | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<CreateGlossaryTermRequest>({
    term: "",
    sql_expression: "",
    aliases: [],
    target_tables: [],
    category: "financial",
    description: "",
    priority: 100,
  });
  const [aliasInput, setAliasInput] = useState("");

  // Delete dialog state
  const [deleteTerm, setDeleteTerm] = useState<GlossaryTerm | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load data sources on mount or org change
  useEffect(() => {
    if (token) {
      loadDataSources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedOrganisation?.organisationId]);

  // Load glossary when data source changes
  useEffect(() => {
    if (
      currentDataSourceId &&
      !loadingDataSources &&
      dataSources.length > 0 &&
      token
    ) {
      loadGlossary();
    } else if (!currentDataSourceId) {
      setTerms([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDataSourceId, loadingDataSources, dataSources.length, token]);

  const loadDataSources = async () => {
    if (!token) return;
    setLoadingDataSources(true);
    try {
      const response = await sqlRagApi.listDataSources(token);
      setDataSources(response.filter((ds) => ds.is_active));
    } catch (error) {
      console.error("Failed to load data sources:", error);
      toast.error("Failed to load data sources");
    } finally {
      setLoadingDataSources(false);
    }
  };

  const loadGlossary = async () => {
    if (!currentDataSourceId || !token) return;
    setLoading(true);
    try {
      const response = await sqlRagApi.listGlossaryTerms(
        token,
        currentDataSourceId,
        undefined,
        true,
      );
      setTerms(response);
    } catch (error) {
      console.error("Failed to load glossary:", error);
      toast.error("Failed to load glossary");
    } finally {
      setLoading(false);
    }
  };

  const handleDataSourceChange = (dataSourceId: string) => {
    setCurrentDataSourceId(dataSourceId);
    setActiveCategory("all");
  };

  const openCreateDialog = () => {
    setEditingTerm(null);
    setFormData({
      term: "",
      sql_expression: "",
      aliases: [],
      target_tables: [],
      category: "financial",
      description: "",
      priority: 100,
    });
    setAliasInput("");
    setShowDialog(true);
  };

  const openEditDialog = (term: GlossaryTerm) => {
    setEditingTerm(term);
    setFormData({
      term: term.term,
      sql_expression: term.sql_expression,
      aliases: term.aliases || [],
      target_tables: term.target_tables || [],
      category: term.category || "general",
      description: term.description || "",
      priority: term.priority,
    });
    setAliasInput((term.aliases || []).join(", "));
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!currentDataSourceId || !token) return;
    if (!formData.term.trim()) {
      toast.error("Term is required");
      return;
    }
    if (!formData.sql_expression.trim()) {
      toast.error("SQL expression is required");
      return;
    }

    // Parse aliases from comma-separated input
    const aliases = aliasInput
      .split(",")
      .map((a) => a.trim().toLowerCase())
      .filter((a) => a.length > 0);

    setSaving(true);
    try {
      if (editingTerm) {
        // Update existing
        const updateData: UpdateGlossaryTermRequest = {
          sql_expression: formData.sql_expression,
          aliases,
          target_tables: formData.target_tables,
          category: formData.category,
          description: formData.description,
          priority: formData.priority,
        };
        await sqlRagApi.updateGlossaryTerm(
          token,
          currentDataSourceId,
          editingTerm.id,
          updateData,
        );
        toast.success("Term updated");
      } else {
        // Create new
        await sqlRagApi.createGlossaryTerm(token, currentDataSourceId, {
          ...formData,
          term: formData.term.toLowerCase().trim(),
          aliases,
        });
        toast.success("Term created");
      }
      setShowDialog(false);
      await loadGlossary();
    } catch (error) {
      console.error("Failed to save term:", error);
      toast.error("Failed to save term");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTerm || !currentDataSourceId || !token) return;

    setDeleting(true);
    try {
      await sqlRagApi.deleteGlossaryTerm(
        token,
        currentDataSourceId,
        deleteTerm.id,
      );
      toast.success("Term deleted");
      setDeleteTerm(null);
      await loadGlossary();
    } catch (error) {
      console.error("Failed to delete term:", error);
      toast.error("Failed to delete term");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (term: GlossaryTerm) => {
    if (!currentDataSourceId || !token) return;
    try {
      await sqlRagApi.updateGlossaryTerm(token, currentDataSourceId, term.id, {
        is_active: !term.is_active,
      });
      toast.success(term.is_active ? "Term deactivated" : "Term activated");
      await loadGlossary();
    } catch {
      toast.error("Failed to update term");
    }
  };

  const filteredTerms = terms.filter((t) => {
    // Filter by source
    if (activeSource !== "all" && t.source !== activeSource) return false;
    // Filter by category
    if (activeCategory !== "all" && t.category !== activeCategory) return false;
    return true;
  });

  // Count terms by source for the filter badges
  const adminCount = terms.filter((t) => t.source === "admin").length;
  const learnedCount = terms.filter((t) => t.source === "learned").length;

  // Get unique categories from terms
  const termCategories = [
    ...new Set(terms.map((t) => t.category || "general")),
  ];

  const selectedDataSource = dataSources.find(
    (ds) => ds.id === currentDataSourceId,
  );

  // Glossary editing is hard-blocked server-side for connector-backed sources
  // (D4b: every non-viewer operation on a projected row answers 409). Without
  // this the page renders the full editor, the user types a definition, and
  // the save comes back 409 — the backend rule is right, the UI just never
  // told them. Read-only stays available: viewing a projected source's
  // glossary is a viewer-level action and works.
  const glossaryIsReadOnly = selectedDataSource?.source === "b2c-connector";
  const canEditThisGlossary = canEditGlossary && !glossaryIsReadOnly;
  const canCreateThisGlossary = canCreateGlossary && !glossaryIsReadOnly;

  return (
    <TooltipProvider>
      <div className="container mx-auto py-3 md:py-6 space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900">
              Business Glossary
            </h1>
            <p className="mt-1 md:mt-2 text-sm md:text-lg text-slate-600">
              Define what business terms mean in SQL to improve query accuracy
            </p>
          </div>
          {currentDataSourceId && canCreateThisGlossary && (
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Add Term
            </Button>
          )}
        </div>

        {/* Data Source Selector */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="h-5 w-5" />
              Select Data Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingDataSources ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading data sources...
              </div>
            ) : dataSources.length === 0 ? (
              <p className="text-muted-foreground">
                No data sources configured. Go to Data Sources to add one.
              </p>
            ) : (
              <Select
                value={currentDataSourceId || ""}
                onValueChange={handleDataSourceChange}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Choose a data source..." />
                </SelectTrigger>
                <SelectContent>
                  {dataSources.map((ds) => (
                    <SelectItem key={ds.id} value={ds.id}>
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4" />
                        <span>{ds.name}</span>
                        {ds.retired_at_utc && (
                          <Badge variant="secondary" className="text-xs">
                            Retired
                          </Badge>
                        )}
                        <Badge variant="outline" className="ml-2 text-xs">
                          {ds.database_type}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>

        {!currentDataSourceId ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">Select a Data Source</h3>
              <p className="text-muted-foreground max-w-md">
                Choose a data source above to view and manage its business
                glossary terms.
              </p>
            </CardContent>
          </Card>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Info Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  About Business Glossary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  The business glossary defines what business terms mean in SQL
                  terms. When users ask about "revenue", the system knows
                  exactly which column to use. This eliminates ambiguity and
                  ensures consistent query results.
                </p>
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Example mappings:</p>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>
                      <code className="bg-background px-1 rounded">
                        revenue
                      </code>{" "}
                      →{" "}
                      <code className="bg-background px-1 rounded">
                        TotalIncTax
                      </code>{" "}
                      (including VAT)
                    </li>
                    <li>
                      <code className="bg-background px-1 rounded">
                        net_revenue
                      </code>{" "}
                      →{" "}
                      <code className="bg-background px-1 rounded">
                        TotalExTax
                      </code>{" "}
                      (excluding VAT)
                    </li>
                    <li>
                      <code className="bg-background px-1 rounded">
                        customer
                      </code>{" "}
                      →{" "}
                      <code className="bg-background px-1 rounded">
                        CustomerName
                      </code>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Terms Table */}
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <CardTitle>
                      Glossary Terms ({filteredTerms.length})
                      {selectedDataSource && (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          - {selectedDataSource.name}
                        </span>
                      )}
                    </CardTitle>
                    {canCreateThisGlossary && (
                      <Button onClick={openCreateDialog}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Term
                      </Button>
                    )}
                    {/* Say WHY the controls are absent. A silently read-only
                        page reads as a bug or a permissions problem; this is
                        neither, and the user can act on the explanation. */}
                    {glossaryIsReadOnly && canCreateGlossary && (
                      <p className="text-sm text-muted-foreground">
                        Read-only: this source is maintained automatically from a
                        B2C connector, so its glossary cannot be edited here.
                      </p>
                    )}
                  </div>
                  {/* Filter Row */}
                  <div className="flex items-center justify-between gap-4">
                    {/* Source Filter */}
                    <Tabs
                      value={activeSource}
                      onValueChange={(v) =>
                        setActiveSource(v as "all" | "admin" | "learned")
                      }
                    >
                      <TabsList>
                        <TabsTrigger value="all">
                          All ({terms.length})
                        </TabsTrigger>
                        <TabsTrigger value="admin" className="gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-purple-600" />
                          Admin ({adminCount})
                        </TabsTrigger>
                        <TabsTrigger value="learned" className="gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />
                          Learned ({learnedCount})
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    {/* Category Filter */}
                    {termCategories.length > 1 && (
                      <Tabs
                        value={activeCategory}
                        onValueChange={setActiveCategory}
                      >
                        <TabsList>
                          <TabsTrigger value="all">All Categories</TabsTrigger>
                          {termCategories.map((cat) => (
                            <TabsTrigger key={cat} value={cat}>
                              {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                      </Tabs>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredTerms.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold">
                      No glossary terms yet
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Add business term definitions to improve query accuracy
                      and eliminate ambiguity.
                    </p>
                    {canCreateThisGlossary && (
                      <Button onClick={openCreateDialog}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add First Term
                      </Button>
                    )}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Term</TableHead>
                        <TableHead>SQL Expression</TableHead>
                        <TableHead className="hidden md:table-cell">Source</TableHead>
                        <TableHead className="hidden md:table-cell">Category</TableHead>
                        <TableHead className="hidden lg:table-cell">Usage</TableHead>
                        <TableHead className="hidden lg:table-cell">Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTerms.map((term) => (
                        <TableRow
                          key={term.id}
                          className={!term.is_active ? "opacity-50" : ""}
                        >
                          <TableCell>
                            <div>
                              <code className="text-sm font-semibold bg-muted px-2 py-1 rounded">
                                {term.term}
                              </code>
                              {term.description && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {term.description}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded dark:bg-blue-900/30 dark:text-blue-300">
                              {term.sql_expression}
                            </code>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {term.source === "admin" ? (
                              <Badge
                                variant="default"
                                className="bg-purple-600"
                              >
                                Admin
                              </Badge>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="secondary">Learned</Badge>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-xs">
                                    Learned from:{" "}
                                    {term.learned_from_query ||
                                      "User clarification"}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    By: {term.created_by}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Badge variant="outline">
                              <Tag className="h-3 w-3 mr-1" />
                              {term.category || "general"}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <span className="text-sm text-muted-foreground">
                              {term.usage_count || 0}
                            </span>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {canEditThisGlossary ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleActive(term)}
                                  >
                                    {term.is_active ? (
                                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    ) : (
                                      <XCircle className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {term.is_active
                                    ? "Click to deactivate"
                                    : "Click to activate"}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              term.is_active ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-muted-foreground" />
                              )
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canEditThisGlossary && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(term)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              {canDeleteGlossary && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteTerm(term)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingTerm ? "Edit Term" : "Create Glossary Term"}
              </DialogTitle>
              <DialogDescription>
                Define what a business term means in SQL. The system will use
                this mapping when generating queries.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="term">
                  Business Term <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="term"
                  placeholder="e.g., revenue, customer, order_date"
                  value={formData.term}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, term: e.target.value }))
                  }
                  disabled={!!editingTerm}
                />
                {!editingTerm && (
                  <p className="text-xs text-muted-foreground">
                    The term users will mention in their questions (lowercase,
                    no spaces)
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sql_expression">
                  SQL Expression <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="sql_expression"
                  placeholder="e.g., TotalIncTax, CustomerName, SUM(Qty)"
                  value={formData.sql_expression}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sql_expression: e.target.value,
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  The actual column name or SQL expression to use
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="aliases">Aliases</Label>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Alternative terms that mean the same thing. E.g., "sales,
                      turnover, income" all map to "revenue".
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Input
                  id="aliases"
                  placeholder="e.g., sales, turnover, income"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Comma-separated list of aliases
                </p>
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, category: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="What does this term represent in your business context?"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : editingTerm ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={!!deleteTerm} onOpenChange={() => setDeleteTerm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Glossary Term</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the term "{deleteTerm?.term}"?
                The system will no longer understand this business term when
                generating queries.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteTerm(null)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
